// services/auth.ts
import axiosInstance from './axiosCreator';
import { User, UpdateUserInfoRequest } from '../types/User';
import { store } from '../store/store';
import {
  authFailed,
  authLoading,
  authSuccess,
  logout,
  setTokens,
} from '../slices/auth/authSlice';
import axios from 'axios';
import {
  userFailed,
  userLoading,
  userReceived,
  userUpdateReceived,
} from '../slices/user/userSlice';

interface UserCredentials {
  email: string;
  password: string;
}

interface RegisterInfo {
  email: string;
  password: string;
  timezone?: string;
  cohort?: string | null;
}

interface AuthResponse {
  token: string;
  refreshToken: string;
  userId: string;
  isAuthenticated: boolean;
}

export interface RegisterResponse {
  message: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Login function - Updated to dispatch authSuccess
export const login = async (
  credentials: UserCredentials
): Promise<ApiResponse<AuthResponse>> => {
  try {
    store.dispatch(authLoading());
    const { data: response } = await axiosInstance.post<
      ApiResponse<AuthResponse>
    >('auth/login', credentials);

    if (!response.success || !response.data) {
      store.dispatch(authFailed(response.error || 'Login failed'));
      return response;
    }

    const { token, refreshToken, userId, isAuthenticated } = response.data;

    store.dispatch(
      authSuccess({
        authToken: token,
        refreshToken,
        userId,
        isAuthenticated,
      })
    );

    return response;
  } catch (error) {
    // @ts-expect-error - ignore type error
    return error?.response?.data;
  }
};

// Create a new user
export const register = async (credentials: RegisterInfo) => {
  try {
    const { data: response } = await axiosInstance.post<
      ApiResponse<RegisterResponse>
    >('auth/register', credentials);
    return response;
  } catch (error) {
    // @ts-expect-error - ignore type error
    return error?.response?.data;
  }
};

// Refresh auth token - Updated to dispatch authSuccess
export const refreshToken = async (currentRefreshToken: string) => {
  try {
    store.dispatch(authLoading());
    const plainAxios = axios.create({
      // Renamed to avoid confusion
      baseURL: import.meta.env.VITE_BACKEND_URL,
      headers: { 'Content-Type': 'application/json' },
    });
    const { data: response } = await plainAxios.post<ApiResponse<AuthResponse>>(
      'auth/refresh',
      {
        refreshToken: currentRefreshToken,
      }
    );

    if (!response.success || !response.data) {
      store.dispatch(authFailed(response.error || 'Refresh failed'));
      throw new Error(response.error || 'Refresh failed');
    }

    const {
      token: newToken,
      refreshToken: newRefreshToken,
      userId,
      isAuthenticated,
    } = response.data;

    store.dispatch(
      authSuccess({
        authToken: newToken,
        refreshToken: newRefreshToken,
        userId,
        isAuthenticated,
      })
    );

    return response.data;
  } catch (error) {
    console.error('Token refresh failed:', error);
    store.dispatch(logout());
  }
};

// Forgot password
export const forgotPassword = async (email: string) => {
  try {
    const response = await axiosInstance.post('auth/forgotPassword', {
      email: email,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Reset password
export const resetPassword = async (
  resetToken: string,
  newPassword: string
) => {
  try {
    const response = await axiosInstance.post('auth/resetPassword', {
      resetToken: resetToken,
      newPassword: newPassword,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Change password
export const changePassword = async (
  token: string,
  oldPassword: string,
  newPassword: string
) => {
  try {
    store.dispatch(authLoading());
    const response = await axiosInstance.post('auth/changePassword', {
      token: token,
      oldPassword: oldPassword,
      newPassword: newPassword,
    });

    if (!response.data.success || !response.data.data) {
      store.dispatch(
        authFailed(response.data.error || 'Failed to change password')
      );
      throw new Error(response.data.error || 'Failed to change password');
    }

    const {
      token: authToken,
      refreshToken,
      userId,
      isAuthenticated,
    } = response.data.data;

    store.dispatch(
      authSuccess({
        authToken,
        refreshToken,
        userId,
        isAuthenticated,
      })
    );

    return response.data;
  } catch (error) {
    console.error('Failed to change password:', error);
    return {
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};

export const getUserInfo = async (): Promise<User | undefined> => {
  try {
    store.dispatch(userLoading());
    const response =
      await axiosInstance.get<ApiResponse<User>>('/auth/profile');

    if (!response.data.success || !response.data.data) {
      store.dispatch(
        userFailed(response.data.error || 'Failed to get user info')
      );
      console.error('Failed to get user info:', response.data.error);
      return undefined;
    }

    store.dispatch(userReceived(response.data.data));
    return response.data.data;
  } catch (error: unknown) {
    // Handle specific error cases as documented
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 401:
          console.error('Authentication error:', error.response.data.error);
          // You might want to trigger a logout here
          store.dispatch(logout());
          break;
        case 404:
          console.error('User not found:', error.response.data.error);
          break;
        case 500:
          console.error('Server error:', error.response.data.error);
          break;
        default:
          console.error('Failed to get user info:', error);
      }
    } else {
      console.error('Unexpected error:', error);
    }
    return undefined;
  }
};

// Update User Info - This should ideally return the updated User
// And the calling code should dispatch userUpdateReceived
export const updateCurrentUserInfo = async (
  userInfo: Partial<UpdateUserInfoRequest>
): Promise<User | undefined> => {
  try {
    store.dispatch(userLoading());
    const response = await axiosInstance.post<ApiResponse<User>>(
      '/auth/updateProfile',
      userInfo
    );

    if (!response.data.success || !response.data.data) {
      store.dispatch(
        userFailed(response.data.error || 'Failed to update user info')
      );
      throw new Error('Failed to update user info');
    }

    store.dispatch(userUpdateReceived(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error('Failed to update user info:', error);
    return undefined;
  }
};

// Impersonate User - Dispatch setTokens
export const impersonateUser = async (userId: string) => {
  try {
    const response = await axiosInstance.post<
      ApiResponse<{
        token: string;
        refreshToken: string;
        userId: string;
        isAuthenticated: boolean;
      }>
    >('/auth/impersonate', {
      userId,
    });

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to impersonate user');
    }

    const {
      token,
      refreshToken,
      userId: impersonatedUserId,
      isAuthenticated,
    } = response.data.data;

    store.dispatch(
      setTokens({
        authToken: token,
        refreshToken,
        userId: impersonatedUserId,
        isAuthenticated,
      })
    );

    return response.data.data;
  } catch (error) {
    console.error('Failed to impersonate user:', error);
    return undefined;
  }
};
